import React from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, WifiOff } from 'lucide-react';
import { useTenant } from './TenantProvider';
import { Skeleton } from '@shared/components/ui/skeleton';
import { Button } from '@shared/components/ui/button';

interface TenantGuardProps {
  children: React.ReactNode;
  /** Custom loading component to show while tenant is resolving */
  loadingComponent?: React.ReactNode;
}

/**
 * Guards children from rendering until tenant/restaurant is resolved.
 * Shows loading skeleton while resolving.
 * On error, shows an inline banner with retry instead of blocking the UI.
 */
export function TenantGuard({
  children,
  loadingComponent,
}: TenantGuardProps) {
  const { isLoading, restaurant, error, errorCode, refetch } = useTenant();

  if (isLoading) {
    return loadingComponent ?? <TenantLoadingFallback />;
  }

  if (!restaurant) {
    // Fatal errors (no subdomain, not found, inactive) → redirect to meitre.com in production
    const isFatalError = errorCode === 'NO_SUBDOMAIN' || errorCode === 'RESTAURANT_NOT_FOUND' || errorCode === 'RESTAURANT_INACTIVE';
    if (isFatalError && !import.meta.env.DEV) {
      window.location.href = 'https://meitre.com';
      return <TenantLoadingFallback />;
    }

    return <TenantErrorPage errorCode={errorCode} onRetry={() => window.location.reload()} />;
  }

  return <>{children}</>;
}

function TenantLoadingFallback() {
  return (
    <div className="min-h-screen bg-card flex flex-col">
      {/* Header skeleton */}
      <header className="px-6 py-4 flex items-center justify-between border-b border-border max-w-2xl mx-auto w-full">
        <Skeleton className="h-8 w-12" />
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-8 w-8 rounded-full" />
      </header>

      {/* Content skeleton */}
      <main className="flex-1 p-6 max-w-2xl mx-auto w-full">
        <div className="text-center mb-8">
          <Skeleton className="h-10 w-48 mx-auto mb-3" />
          <Skeleton className="h-6 w-32 mx-auto" />
        </div>

        <div className="space-y-4">
          <Skeleton className="h-14 w-full rounded-xl" />
          <Skeleton className="h-12 w-full rounded-xl" />
          <div className="grid grid-cols-2 gap-3">
            <Skeleton className="h-12 rounded-xl" />
            <Skeleton className="h-12 rounded-xl" />
          </div>
        </div>

        <div className="mt-10">
          <Skeleton className="h-8 w-40 mb-4" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-48 rounded-xl" />
            <Skeleton className="h-48 rounded-xl" />
          </div>
        </div>
      </main>
    </div>
  );
}

function TenantErrorPage({
  errorCode,
  onRetry,
}: {
  errorCode: string | null;
  onRetry: () => void;
}) {
  const { t } = useTranslation('errors');

  const isNetworkError = errorCode === 'NETWORK_ERROR' || errorCode === 'TIMEOUT_ERROR';
  const Icon = isNetworkError ? WifiOff : RefreshCw;
  const title = isNetworkError
    ? t('TENANT_NETWORK_ERROR')
    : t('GENERAL_SOMETHING_WENT_WRONG');

  return (
    <div className="min-h-screen bg-card flex flex-col items-center justify-center p-6">
      <div className="max-w-sm w-full text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <Icon className="h-8 w-8 text-muted-foreground" />
        </div>
        <h1 className="text-lg font-semibold text-foreground mb-2">{title}</h1>
        <p className="text-sm text-muted-foreground mb-8">
          {t('GENERAL_TRY_AGAIN')}
        </p>
        <Button className="w-full" size="lg" onClick={onRetry}>
          <RefreshCw size={16} className="mr-2" />
          {t('ACTIONS_RETRY')}
        </Button>
      </div>
    </div>
  );
}

export default TenantGuard;
